// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import { useAppDispatch } from '@advisor-app/store/store';
import { type AnyAction } from '@reduxjs/toolkit';
import { type SetFactorsFilter } from '@shared/data-access/interfaces/redux/parameters-state';
import { Heading, type PopupsName } from '@shared/ui-kit/components';
import { useCallback, useState } from 'react';
import { Slider } from './slider';

interface VerticalSliderProps {
  state: number[];
  name: string;
  index: number;
  min?: number;
  max?: number;
  saveValuesOnRedux: (factorValues: SetFactorsFilter) => AnyAction;
}

export const VerticalSlider = ({
  min,
  max,
  index,
  state,
  name,
  saveValuesOnRedux,
}: VerticalSliderProps) => {
  const [values, setValues] = useState<number[]>(state);

  const dispatch = useAppDispatch();
  /* istanbul ignore next */
  const setValueOnRedux = useCallback(
    (value: number[]) => {
      const payload = {
        index,
        value,
      };
      dispatch(saveValuesOnRedux(payload));
    },
    [dispatch, index, saveValuesOnRedux],
  );

  return (
    <div className="mb-2 w-[150px]">
      <div className="mb-4 flex justify-center">
        <Heading
          variant="h5"
          title={capitalizeString(name)}
          popupName={name as PopupsName}
        />
      </div>
      <div className="flex justify-center">
        <Slider
          value={values}
          onChangeCommitted={
            /* istanbul ignore next */ (_, newValue) =>
              setValueOnRedux(newValue as number[])
          }
          onChange={
            /* istanbul ignore next */ (_, newValue) => {
              setValues(newValue as number[]);
            }
          }
          valueLabelDisplay="off"
          min={min ?? 0}
          max={max ?? 100}
          orientation="vertical"
          valueLabelFormat={value => `${value}%`}
          enableInput
        />
      </div>
    </div>
  );
};
